/* eslint-disable prefer-arrow-callback */
import React, { useContext, useEffect, useMemo } from "react";

import { useAppSetting, usePrevious } from "@swa-ui/application";
import { getBootstrapData } from "@swa-ui/bootstrap";
import { useIsMounted } from "@swa-ui/browser";
import { Dialog } from "@swa-ui/core";
import {
  encodeMessage,
  isHybridEnabled,
  NATIVE_APP_INTERFACE_NAMES,
  sendMessageToNativeApp,
} from "@swa-ui/hybrid";
import i18n from "@swa-ui/locale";

import { AuthContext } from "../../AuthProvider";
import { AuthResponseHandlerContext } from "../../AuthResponseHandler";
import {
  LOGIN_FORM_TYPE,
  MFA_FORM_TYPE,
  RE_AUTHENTICATION_FORM_TYPE,
} from "../../defines/formTypes";
import { useUserInfo } from "../../UserInfoProvider";
import { LoginForm } from "../LoginForm";
import { MfaAuthorizer } from "../MfaAuthorizer";
import { MissingInformationDialog } from "../MissingInformationDialog";

/**
 * Renders Login or Missing Information Dialog based on response handling.
 */

export const AuthResponseDialog = () => {
  const {
    activeForm,
    logoutTrigger,
    pendingRetry,
    shouldCleanUpSession,
    setHasExistingAccountInfo,
  } = useContext(AuthResponseHandlerContext);
  const {
    appendNativeLoginCancelHandlers,
    appendNativeLoginHandlers,
    cleanUpSession,
    isLimitedAccess,
    handleLogout,
    missingCorporateInformation,
    missingInformation,
  } = useContext(AuthContext);
  const onReAuthCancel = useAppSetting("onReAuthCancel", handleReAuthCancel);
  const reAuthCancelButtonText = useAppSetting("reAuthCancelButtonText");
  const dialogBody = useMemo(renderActiveForm, [
    activeForm?.type,
    onReAuthCancel,
    reAuthCancelButtonText,
  ]); // NOSONAR
  const previousCancel = usePrevious(pendingRetry?.cancel);
  const previousNext = usePrevious(pendingRetry?.next);
  const { currentAccountNumber } = useUserInfo();
  const isMounted = useIsMounted();

  useEffect(
    function handleCleanUpSession() {
      shouldCleanUpSession && cleanUpSession();
      shouldCleanUpSession &&
        isHybridEnabled() &&
        sendMessageToNativeApp(NATIVE_APP_INTERFACE_NAMES.LOGOUT);
    },
    [shouldCleanUpSession]
  );

  useEffect(() => {
    if (isMounted) {
      handleLogout();
    }
  }, [logoutTrigger]);

  useEffect(
    function handleDisplayNativeLogin() {
      if (isHybridEnabled() && pendingRetry?.next && !previousNext) {
        const message = encodeMessage({
          continueAsGuest: false,
          loginType: "normal",
        });

        sendMessageToNativeApp(NATIVE_APP_INTERFACE_NAMES.DISPLAY_LOGIN, message);
        appendNativeLoginHandlers(pendingRetry?.next, pendingRetry?.url);
      }
    },
    [pendingRetry?.next]
  );

  useEffect(() => {
    setHasExistingAccountInfo(!!currentAccountNumber);
  }, [currentAccountNumber]);

  useEffect(
    function handleNativeLoginCancel() {
      if (isHybridEnabled() && pendingRetry?.cancel && !previousCancel) {
        const onNativeLoginCancel = () => {
          pendingRetry?.cancel?.();
          sendMessageToNativeApp(NATIVE_APP_INTERFACE_NAMES.EXIT);
        };

        appendNativeLoginCancelHandlers(onNativeLoginCancel, pendingRetry?.url);
      }
    },
    [pendingRetry?.cancel]
  );

  return renderResponseDialog();

  function renderResponseDialog() {
    let dialog;

    if (!isHybridEnabled() && dialogBody) {
      dialog = <Dialog {...getLoginDialogProps()}>{dialogBody}</Dialog>;
    } else if (shouldDisplayMissingInformationDialog()) {
      dialog = (
        <MissingInformationDialog
          missingInformation={missingInformation}
          missingCorporateInformation={missingCorporateInformation}
        />
      );
    }

    return dialog;
  }

  function getLoginDialogProps() {
    return {
      "aria-label": i18n("LoginForm__TITLE"),
      buttonGroupProps: {
        "aria-label-next": i18n("AuthResponseDialog__BUTTON_GROUP_ARIA_NEXT"),
        "aria-label-previous": i18n("AuthResponseDialog__BUTTON_GROUP_ARIA_PREVIOUS"),
      },
      onClose: activeForm?.type === MFA_FORM_TYPE ? handleMfaCancel : handleCancel,
      portal: false,
      revealed: !!dialogBody,
      showClose: true,
      width: "small",
    };
  }

  function renderActiveForm() {
    const formType = activeForm?.type;
    const forms = {
      [LOGIN_FORM_TYPE]: renderLoginForm,
      [MFA_FORM_TYPE]: renderMfaAuthorizer,
      [RE_AUTHENTICATION_FORM_TYPE]: renderLoginForm,
    };

    return formType && forms[formType]?.();
  }

  function renderLoginForm() {
    const loginFormProps = {
      additionalButtonProps: {
        ...(reAuthCancelButtonText && { children: reAuthCancelButtonText }),
        onClick: handleCancel,
      },
      formType: activeForm?.type,
      onLoggedIn: handleLoggedIn,
    };

    return <LoginForm {...loginFormProps} />;
  }

  function renderMfaAuthorizer() {
    return <MfaAuthorizer onAuthorized={handleMfaAuthorized} onCancel={handleMfaCancel} />;
  }

  function shouldDisplayMissingInformationDialog() {
    return (
      !isHybridEnabled() &&
      isLimitedAccess() &&
      ((Array.isArray(missingInformation) && missingInformation.length > 0) ||
        (Array.isArray(missingCorporateInformation) && missingCorporateInformation.length > 0))
    );
  }

  function handleCancel() {
    pendingRetry?.cancel?.();
    cleanUpSession();
    onReAuthCancel();
  }

  function handleLoggedIn() {
    pendingRetry?.next?.();
  }

  function handleMfaAuthorized() {
    pendingRetry?.next?.();
  }

  function handleMfaCancel() {
    pendingRetry?.cancel?.();
  }

  function handleReAuthCancel() {
    const { HOME_PAGE } = getBootstrapData("urls");

    window.location.replace(HOME_PAGE);
  }
};
